import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdMarkEmailRead } from 'react-icons/md';
import { fetchCurrencies, fetchIntegrationRegistrationStatus, fetchOrganizationTypes, registerCompany } from '../../../services/Http/Api';
import Logo from '../../../assets/images/dwlogo_wide.png';
import LogoHeader from '../../../shared/layout/components/misc/LogoHeader';
import LanguageSelector from '../../../shared/layout/components/misc/LanguageSelector';
import GlassCard from "../../../shared/layout/components/cards/GlassCard";
import GradientBackground from "../../../shared/layout/components/containers/GradientBackground";
// import StyledCard from '../../../shared/layout/components/cards/StyledCard'; // Replaced
import styled from 'styled-components';
import MainButton from '../../../shared/layout/buttons/MainButton';
import { SearchableSelect, SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const RegisterCompany = ({ embedded = false, onCancel }) => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const [registrationContext] = useState(getRegistrationContext);
    const integrationContext = registrationContext?.sessionId ? registrationContext : null;
    const isWooCommerceSignup = registrationContext?.source === 'woocommerce' && !integrationContext;
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('selectedLanguage') || 'en');
    const [organizationTypes, setOrganizationTypes] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [formData, setFormData] = useState({
        companyName: '',
        organizationTypeId: '',
        currencyId: '',
        adminName: '',
        adminSurname: '',
        adminEmail: '',
        password: '',
        passwordConfirmation: '',
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [registrationResult, setRegistrationResult] = useState(null);
    const [registrationStatus, setRegistrationStatus] = useState('pending_verification');
    const registrationSessionId = registrationResult?.sessionId;

    useEffect(() => {
        const sessionId = registrationSessionId;
        if (!sessionId) return undefined;

        let active = true;
        let finished = false;
        let checking = false;
        let timer;
        const terminalStatuses = new Set(['approved', 'awaiting_review', 'rejected', 'expired', 'unavailable']);
        const checkStatus = async () => {
            if (!active || finished || checking || document.visibilityState === 'hidden') return;
            checking = true;
            try {
                const result = await fetchIntegrationRegistrationStatus(sessionId);
                if (!active || !result?.status) return;
                setRegistrationStatus(result.status);
                if (terminalStatuses.has(result.status)) {
                    finished = true;
                    window.clearInterval(timer);
                }
            } catch (_error) {
                // A temporary network failure should not interrupt email verification.
            } finally {
                checking = false;
            }
        };

        const checkOnReturn = () => {
            if (document.visibilityState === 'visible') checkStatus();
        };

        checkStatus();
        timer = window.setInterval(checkStatus, 20000);
        window.addEventListener('focus', checkOnReturn);
        document.addEventListener('visibilitychange', checkOnReturn);

        return () => {
            active = false;
            window.clearInterval(timer);
            window.removeEventListener('focus', checkOnReturn);
            document.removeEventListener('visibilitychange', checkOnReturn);
        };
    }, [registrationSessionId]);

    useEffect(() => {
        const loadOrganizationMeta = async () => {
            try {
                const [types, currencyList] = await Promise.all([
                    fetchOrganizationTypes(),
                    fetchCurrencies(),
                ]);
                setOrganizationTypes(types || []);
                setCurrencies(currencyList || []);
                if (integrationContext || isWooCommerceSignup) {
                    const retail = (types || []).find((type) => Number(type.id) === 2);
                    setFormData((current) => ({
                        ...current,
                        organizationTypeId: retail ? String(retail.id) : '2',
                    }));
                }
            } catch (err) {
                console.error('Failed to load organization registration metadata:', err);
            }
        };

        loadOrganizationMeta();
    }, []);

    useEffect(() => {
        i18n.changeLanguage(selectedLanguage);
    }, [i18n, selectedLanguage]);

    const handleLanguageChange = (language) => {
        setSelectedLanguage(language);
        localStorage.setItem('selectedLanguage', language);
        i18n.changeLanguage(language);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => ({ ...prev, [name]: false }));
    };

    const handleSelectChange = (fieldName, value) => {
        setFormData((prev) => ({ ...prev, [fieldName]: value || '' }));
        setFieldErrors((prev) => ({ ...prev, [fieldName]: false }));
    };

    const getSelectErrorStyles = (hasError) => (hasError ? {
        control: (base, state) => ({
            ...base,
            borderColor: '#dc2626',
            boxShadow: state.isFocused ? '0 0 0 4px rgba(220, 38, 38, 0.12)' : base.boxShadow,
            '&:hover': {
                borderColor: '#dc2626',
            },
        }),
    } : undefined);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        const nextErrors = {};

        if (!formData.companyName?.trim()) nextErrors.companyName = true;
        if (!formData.organizationTypeId) nextErrors.organizationTypeId = true;
        if (!formData.currencyId) nextErrors.currencyId = true;
        if (!formData.adminName?.trim()) nextErrors.adminName = true;
        if (!formData.adminSurname?.trim()) nextErrors.adminSurname = true;
        if (!formData.adminEmail?.trim()) nextErrors.adminEmail = true;
        if (!formData.password) nextErrors.password = true;
        if (!formData.passwordConfirmation) nextErrors.passwordConfirmation = true;
        setFieldErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            setError(t('RegisterPage.fix_required', 'Please fill all required fields.'));
            return;
        }

        if (formData.password !== formData.passwordConfirmation) {
            setFieldErrors((prev) => ({ ...prev, password: true, passwordConfirmation: true }));
            setError(t('RegisterPage.passwords_mismatch', 'Passwords do not match.'));
            return;
        }

        setLoading(true);
        try {
            const response = await registerCompany({
                company_name: formData.companyName,
                organization_type_id: Number(formData.organizationTypeId),
                currency_id: Number(formData.currencyId),
                admin_name: formData.adminName,
                admin_surname: formData.adminSurname,
                admin_email: formData.adminEmail,
                password: formData.password,
                password_confirmation: formData.passwordConfirmation,
                ...(isWooCommerceSignup ? { registration_source: 'woocommerce' } : {}),
                ...(integrationContext ? {
                    registration_source: integrationContext.platform,
                    integration_session_id: integrationContext.sessionId,
                } : {}),
            });
            if (response?.status !== 'pending_verification') {
                throw new Error('Unexpected registration response');
            }
            setRegistrationResult({
                email: formData.adminEmail,
                companyName: response?.organization?.name || formData.companyName,
                platform: response?.onboarding?.platform || registrationContext?.source,
                sessionId: response?.onboarding?.session_id || integrationContext?.sessionId,
            });
        } catch (err) {
            console.error('Company registration failed:', err);
            const backendErrors = err?.response?.data?.errors || err?.response?.data;
            if (backendErrors && typeof backendErrors === 'object') {
                const map = {
                    company_name: 'companyName',
                    organization_type_id: 'organizationTypeId',
                    currency_id: 'currencyId',
                    admin_name: 'adminName',
                    admin_surname: 'adminSurname',
                    admin_email: 'adminEmail',
                    password: 'password',
                    password_confirmation: 'passwordConfirmation',
                };
                const mapped = {};
                Object.keys(backendErrors).forEach((key) => {
                    if (map[key]) {
                        mapped[map[key]] = true;
                    }
                });
                if (Object.keys(mapped).length > 0) {
                    setFieldErrors((prev) => ({ ...prev, ...mapped }));
                }
            }
            setError(t('RegisterPage.registration_failed', 'Registration failed. Please check the form and try again.'));
        } finally {
            setLoading(false);
        }
    };

    const registrationStatusMessages = {
        pending_verification: t('RegisterPage.verification_pending', 'Waiting for email confirmation. This page checks automatically every 20 seconds.'),
        approved: t('RegisterPage.verification_approved', 'Email confirmed and your retailer account is active. You can continue to sign in.'),
        awaiting_review: t('RegisterPage.verification_review', 'Email confirmed. Your account is waiting for DigiWarranty review.'),
        rejected: t('RegisterPage.verification_rejected', 'Email confirmed, but the account needs assistance. Please contact DigiWarranty support.'),
        expired: t('RegisterPage.verification_expired', 'This connection has expired. Start the store connection again.'),
        unavailable: t('RegisterPage.verification_unavailable', 'We could not find this registration. Please contact DigiWarranty support.'),
    };

    if (registrationResult) {
        const platformName = registrationResult.platform === 'shopware' ? 'Shopware' : 'WooCommerce';
        return (
            <RegistrationShell embedded={embedded} selectedLanguage={selectedLanguage} onLanguageChange={handleLanguageChange}>
                <SuccessState>
                    <SuccessIcon aria-hidden="true" />
                    <h2>{t('RegisterPage.check_email_title', 'Check your email')}</h2>
                    <p>{t('RegisterPage.check_email_message', {
                        email: registrationResult.email,
                        defaultValue: 'We sent a verification link to {{email}}. Open it to activate your company account.',
                    })}</p>
                    {registrationResult.sessionId && (
                        <SuccessNote role="status" aria-live="polite">
                            {registrationStatusMessages[registrationStatus] || t('RegisterPage.verification_checking', 'Checking email confirmation…')}
                        </SuccessNote>
                    )}
                    {registrationResult.platform && (
                        <SuccessNote>{t('RegisterPage.plugin_return_hint', {
                            platform: platformName,
                            defaultValue: 'After verifying your email and completing any account review, sign in and you will return to this connection to choose your organization and online venue.',
                        })}</SuccessNote>
                    )}
                    {(!registrationResult.sessionId || ['approved', 'verified'].includes(registrationStatus)) && (
                        <MainButton type="button" onClick={() => navigate('/login')}>
                            {t('RegisterPage.back_to_login')}
                        </MainButton>
                    )}
                </SuccessState>
            </RegistrationShell>
        );
    }

    const formContent = (
        <>
            <HeaderText className={embedded ? "mb-4" : ""}>
                <h2>{t('RegisterPage.title')}</h2>
                <p>{t('RegisterPage.subtitle')}</p>
                {isWooCommerceSignup && <p>{t('RegisterPage.woocommerce_signup_note', 'Create your retailer account on the Free plan. You can review upgrade options later in your dashboard. Email verification and account review may be required before you can create a store key.')}</p>}
            </HeaderText>
            <Form onSubmit={handleSubmit}>
                <FormGrid $single={Boolean(integrationContext || isWooCommerceSignup)}>
                    <FormGroup $error={fieldErrors.companyName}>
                        <label>{t('RegisterPage.company_name')}</label>
                        <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            required
                        />
                        {fieldErrors.companyName && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                    </FormGroup>
                    {!integrationContext && !isWooCommerceSignup && <FormGroup $error={fieldErrors.organizationTypeId}>
                        <label>{t('RegisterPage.company_type')}</label>
                        <SimpleSelect
                            inputId="register-company-type"
                            value={formData.organizationTypeId}
                            onChange={(_, nextValue) => handleSelectChange('organizationTypeId', String(nextValue || ''))}
                            styles={getSelectErrorStyles(fieldErrors.organizationTypeId)}
                            options={[
                                { value: '', label: t('RegisterPage.select_type') },
                                ...organizationTypes.map((type) => ({
                                    value: String(type.id),
                                    label: type.name,
                                })),
                            ]}
                        />
                        {fieldErrors.organizationTypeId && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                    </FormGroup>}
                </FormGrid>

                <FormGroup $error={fieldErrors.currencyId}>
                    <label>{t('RegisterPage.currency', 'Currency')}</label>
                    <SearchableSelect
                        inputId="register-company-currency"
                        value={formData.currencyId}
                        onChange={(_, nextValue) => handleSelectChange('currencyId', String(nextValue || ''))}
                        isClearable={false}
                        styles={getSelectErrorStyles(fieldErrors.currencyId)}
                        options={[
                            { value: '', label: t('RegisterPage.select_currency', 'Select currency') },
                            ...currencies.map((currency) => ({
                                value: String(currency.id),
                                label: `${currency.code || currency.key || 'CUR'} - ${currency.name || 'Currency'}`,
                            })),
                        ]}
                    />
                    {fieldErrors.currencyId && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                </FormGroup>

                <FormGrid>
                    <FormGroup $error={fieldErrors.adminName}>
                        <label>{t('RegisterPage.admin_name')}</label>
                        <input
                            type="text"
                            name="adminName"
                            value={formData.adminName}
                            onChange={handleChange}
                            required
                        />
                        {fieldErrors.adminName && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                    </FormGroup>
                    <FormGroup $error={fieldErrors.adminSurname}>
                        <label>{t('RegisterPage.admin_surname')}</label>
                        <input
                            type="text"
                            name="adminSurname"
                            value={formData.adminSurname}
                            onChange={handleChange}
                            required
                        />
                        {fieldErrors.adminSurname && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                    </FormGroup>
                </FormGrid>

                <FormGroup $error={fieldErrors.adminEmail}>
                    <label>{t('RegisterPage.admin_email')}</label>
                    <input
                        type="email"
                        name="adminEmail"
                        value={formData.adminEmail}
                        onChange={handleChange}
                        required
                    />
                    {fieldErrors.adminEmail && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                </FormGroup>
                <FormGrid>
                    <FormGroup $error={fieldErrors.password}>
                        <label>{t('RegisterPage.password')}</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                        {fieldErrors.password && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                    </FormGroup>
                    <FormGroup $error={fieldErrors.passwordConfirmation}>
                        <label>{t('RegisterPage.password_repeat', 'Repeat Password')}</label>
                        <input
                            type="password"
                            name="passwordConfirmation"
                            value={formData.passwordConfirmation}
                            onChange={handleChange}
                            required
                        />
                        {fieldErrors.passwordConfirmation && <FieldError>{t('RegisterPage.field_required', 'This field is required.')}</FieldError>}
                    </FormGroup>
                </FormGrid>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <MainButton type="submit" disabled={loading} style={{ marginTop: '10px' }}>
                    {loading && <LoadingSpinner aria-hidden="true" />}
                    {loading ? t('RegisterPage.registering') : t('RegisterPage.submit')}
                </MainButton>

                <SecondaryLink type="button" onClick={() => (onCancel ? onCancel() : navigate('/login'))}>
                    {t('RegisterPage.back_to_login')}
                </SecondaryLink>
            </Form>
        </>
    );

    if (embedded) {
        return formContent;
    }

    return <RegistrationShell embedded={false} selectedLanguage={selectedLanguage} onLanguageChange={handleLanguageChange}>{formContent}</RegistrationShell>;
};

const getRegistrationContext = () => {
    try {
        const url = new URL(window.location.href);
        const source = url.searchParams.get('source');
        if (source === 'woocommerce') return { source };

        const lastVisitedPath = localStorage.getItem('lastVisitedPath');
        if (!lastVisitedPath) return null;
        const lastUrl = new URL(lastVisitedPath, window.location.origin);
        const match = lastUrl.pathname.match(/^\/connect\/(woocommerce|shopware)$/);
        const sessionId = lastUrl.searchParams.get('session');
        if (!match || !sessionId) return null;

        return { source: match[1], platform: match[1], sessionId };
    } catch (_error) {
        return null;
    }
};

const RegistrationShell = ({ embedded, selectedLanguage, onLanguageChange, children }) => {
    if (embedded) return children;

    return (
        <GradientBackground>
            <GlassCard style={{ maxWidth: '600px' }}>
                <HeaderTop>
                    <div />
                    <LanguageSelector selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} align="flex-end" />
                </HeaderTop>
                <LogoHeader logoSrc={Logo} title="DigiWarranty" />
                {children}
            </GlassCard>
        </GradientBackground>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin-top: 1.5rem;
`;

const HeaderTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
`;

const HeaderText = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;

    h2 {
        font-size: 1.6rem;
        font-weight: 700;
        color: #1a0621;
        margin-bottom: 0.4rem;
    }

    p {
        color: #6b7280;
        font-size: 0.95rem;
        margin: 0;
    }
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ $single }) => ($single ? '1fr' : '1fr 1fr')};
    gap: 0.9375rem;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
    }

    input,
    select {
        padding: 0.75rem 1rem;
        border: 1.5px solid ${({ $error }) => ($error ? '#dc2626' : '#e5e7eb')};
        border-radius: 10px;
        font-size: 0.95rem;
        outline: none;
        transition: all 0.2s ease;
        background: #fff;

        &:focus {
            border-color: ${({ $error }) => ($error ? '#dc2626' : 'rgba(130, 22, 146, 0.5)')};
            background: #fff;
            box-shadow: ${({ $error }) => ($error ? '0 0 0 4px rgba(220, 38, 38, 0.12)' : '0 0 0 4px rgba(130, 22, 146, 0.1)')};
        }
    }
`;

const FieldError = styled.span`
    color: #dc2626;
    font-size: 0.75rem;
    margin-top: -0.125rem;
`;

const ErrorMessage = styled.div`
    color: #dc2626;
    font-size: 0.8125rem;
    background: #fef2f2;
    padding: 0.75rem;
    border-radius: 8px;
    border: 1px solid #fecaca;
`;

const SuccessState = styled.div`
    display: grid;
    justify-items: center;
    gap: 1rem;
    padding: 1.5rem 0 0.5rem;
    text-align: center;

    h2 { margin: 0; color: #1a0621; font-size: 1.6rem; letter-spacing: 0; }
    p { margin: 0; color: #4b5563; line-height: 1.6; overflow-wrap: anywhere; }
`;

const SuccessIcon = styled(MdMarkEmailRead)`
    width: 52px;
    height: 52px;
    color: #16845b;
`;

const SuccessNote = styled.div`
    width: 100%;
    padding: 0.875rem 1rem;
    border: 1px solid #d1fae5;
    border-radius: 8px;
    background: #ecfdf5;
    color: #166534;
    font-size: 0.875rem;
    line-height: 1.5;
`;

const SecondaryLink = styled.button`
    background: none;
    border: none;
    color: rgba(130, 22, 146, 1);
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    margin-top: 1rem;
    width: 100%;
    text-align: center;

    &:hover {
        text-decoration: underline;
    }
`;

const LoadingSpinner = styled.span`
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    display: inline-block;
    animation: spin 0.7s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export default RegisterCompany;
